// ══════════════════════════════════════════════════════════════════════
// GQ Data Loader — OHLCV provider
// Loads bundled CSVs from app/data/ OR generates deterministic synthetic
// series on demand. Also parses uploaded TradingView CSV exports into
// the test_table format already used by the tearsheet.
// ══════════════════════════════════════════════════════════════════════

(function (root) {

  // ────────────────────────────────────────────────────────────────────
  // Synthetic OHLCV — deterministic geometric Brownian with regimes
  // Used as fallback / demo data when CSV not available
  // ────────────────────────────────────────────────────────────────────
  function lcg(seed) {
    let s = seed >>> 0;
    return () => (s = (s * 9301 + 49297) % 233280) / 233280;
  }
  function gaussianRng(rng) {
    return () => {
      let u = 0, v = 0;
      while (u === 0) u = rng();
      while (v === 0) v = rng();
      return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
    };
  }

  const PRESETS = {
    SPY:  { seed: 11, mu: 0.0004, sigma: 0.012, jumpProb: 0.02, jumpSize: 0.02, p0: 380 },
    QQQ:  { seed: 22, mu: 0.0006, sigma: 0.018, jumpProb: 0.025, jumpSize: 0.03, p0: 320 },
    AAPL: { seed: 33, mu: 0.0008, sigma: 0.020, jumpProb: 0.03,  jumpSize: 0.04, p0: 170 },
    MSFT: { seed: 44, mu: 0.0007, sigma: 0.016, jumpProb: 0.02,  jumpSize: 0.03, p0: 380 },
    GLD:  { seed: 55, mu: 0.0002, sigma: 0.010, jumpProb: 0.015, jumpSize: 0.025, p0: 190 },
    TLT:  { seed: 66, mu: 0.0001, sigma: 0.011, jumpProb: 0.01,  jumpSize: 0.02, p0: 95 },
    NDAQ: { seed: 77, mu: 0.0005, sigma: 0.015, jumpProb: 0.02,  jumpSize: 0.03, p0: 16000 },
  };

  function generateOHLCV(symbol = 'SPY', opts = {}) {
    const cfg = Object.assign({}, PRESETS[symbol] || PRESETS.SPY, opts);
    const n = opts.bars || 1260;     // ~5 years of daily
    const rng = lcg(cfg.seed);
    const norm = gaussianRng(rng);

    const bars = [];
    let price = cfg.p0;
    // Start date: bars years back from today
    const today = new Date('2026-01-01');
    today.setDate(today.getDate() - n);

    let regime = 1;  // 1=bull, 0=sideways, -1=bear
    let regimeBarsLeft = 80 + Math.floor(rng() * 120);

    for (let i = 0; i < n; i++) {
      // Switch regime occasionally
      if (regimeBarsLeft-- <= 0) {
        regime = [-1, 0, 1][Math.floor(rng() * 3)];
        regimeBarsLeft = 60 + Math.floor(rng() * 120);
      }
      const regimeMu = cfg.mu * regime;
      const z = norm();
      let r = regimeMu + cfg.sigma * z;
      // Occasional jumps (fat tails)
      if (rng() < cfg.jumpProb) r += (rng() < 0.4 ? -1 : 1) * cfg.jumpSize * (0.5 + rng());

      const open = price;
      const close = price * Math.exp(r);
      const high = Math.max(open, close) * (1 + Math.abs(norm()) * cfg.sigma * 0.5);
      const low  = Math.min(open, close) * (1 - Math.abs(norm()) * cfg.sigma * 0.5);
      const volume = Math.floor(1e6 * (1 + Math.abs(z) * 0.5) * (1 + rng() * 0.3));

      const d = new Date(today); d.setDate(today.getDate() + i);
      bars.push({
        time: d.toISOString().slice(0, 10),
        open, high, low, close, volume,
      });
      price = close;
    }
    return bars;
  }

  // ────────────────────────────────────────────────────────────────────
  // CSV loader — fetches /data/<symbol>.csv first, falls back to synthetic
  // CSV columns: time,open,high,low,close,volume
  //
  // Bundled CSVs cover: 2021-01-04 → 2025-10-31 (6 symbols: SPY, QQQ,
  // AAPL, MSFT, GLD, TLT). Data is deterministic synthetic (seeded), NOT
  // real market data — generated by scripts/generate-ohlcv.js.
  // To regenerate (no external API needed):
  //   node scripts/generate-ohlcv.js
  // ────────────────────────────────────────────────────────────────────
  async function load(symbol = 'SPY', opts = {}) {
    if (root.GQ_DATA_CACHE && root.GQ_DATA_CACHE[symbol]) return root.GQ_DATA_CACHE[symbol];
    let bars = null;
    try {
      const res = await fetch(`data/${symbol}.csv`);
      if (res.ok) bars = parseCSV(await res.text());
    } catch (e) { /* fallthrough */ }
    if (!bars || bars.length < 30) bars = generateOHLCV(symbol, opts);
    root.GQ_DATA_CACHE = root.GQ_DATA_CACHE || {};
    root.GQ_DATA_CACHE[symbol] = bars;
    return bars;
  }
  async function loadMany(symbols, opts = {}) {
    const out = {};
    for (const s of symbols) out[s] = await load(s, opts);
    return out;
  }

  function parseCSV(text) {
    const lines = text.trim().split(/\r?\n/);
    const header = lines[0].toLowerCase().split(',').map(s => s.trim());
    const ix = name => header.indexOf(name);
    const t = ix('time') >= 0 ? ix('time') : ix('date');
    const o = ix('open'), h = ix('high'), l = ix('low'), c = ix('close'), v = ix('volume');
    const out = [];
    for (let i = 1; i < lines.length; i++) {
      const f = lines[i].split(',');
      out.push({
        time:   f[t],
        open:   parseFloat(f[o]),
        high:   parseFloat(f[h]),
        low:    parseFloat(f[l]),
        close:  parseFloat(f[c]),
        volume: v >= 0 ? parseFloat(f[v]) : 0,
      });
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // Equity-curve helper — turn OHLCV into a buy-and-hold benchmark
  // ────────────────────────────────────────────────────────────────────
  function buyAndHold(ohlcv, startEquity = 10000) {
    const first = ohlcv[0].close;
    const eq = [], curve = [];
    for (const b of ohlcv) {
      const v = (b.close / first) * startEquity;
      eq.push(v); curve.push({ t: b.time, v });
    }
    return { equity: eq, curve };
  }

  // ────────────────────────────────────────────────────────────────────
  // Date slicing
  // ────────────────────────────────────────────────────────────────────
  function sliceByDate(ohlcv, fromISO, toISO) {
    const from = fromISO ? new Date(fromISO).getTime() : -Infinity;
    const to   = toISO   ? new Date(toISO).getTime()   :  Infinity;
    return ohlcv.filter(b => {
      const t = new Date(b.time).getTime();
      return t >= from && t <= to;
    });
  }
  // Known historical crisis windows
  const CRISIS_WINDOWS = {
    '2008_gfc':       ['2008-09-01', '2009-03-31'],
    'covid':          ['2020-02-19', '2020-04-30'],
    '2022_rates':     ['2022-01-03', '2022-10-14'],
    'volmageddon':    ['2018-02-01', '2018-02-15'],
    'dotcom':         ['2000-03-10', '2002-10-09'],
    'flash_crash':    ['2010-05-05', '2010-05-10'],
  };

  // ────────────────────────────────────────────────────────────────────
  // TradingView export parser — maps to test_table format
  // ────────────────────────────────────────────────────────────────────
  function parseTradingViewCSV(text) {
    const lines = text.trim().split(/\r?\n/);
    const header = lines[0].split(',').map(s => s.replace(/^"|"$/g, '').trim());
    const out = [];
    for (let i = 1; i < lines.length; i++) {
      // CSV may contain commas inside quoted fields; do a quick split
      const row = parseCSVRow(lines[i]);
      const r = {};
      header.forEach((h, idx) => { r[h] = row[idx]; });
      out.push(r);
    }
    return out;
  }
  function parseCSVRow(line) {
    const out = []; let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { inQ = !inQ; continue; }
      if (c === ',' && !inQ) { out.push(cur); cur = ''; continue; }
      cur += c;
    }
    out.push(cur);
    return out;
  }

  const api = {
    load, loadMany, generateOHLCV, parseCSV, parseTradingViewCSV,
    buyAndHold, sliceByDate, CRISIS_WINDOWS, PRESETS,
  };
  if (root) { root.GQ = root.GQ || {}; root.GQ.data = api; }
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
